<template>
  <section
    class="quote-video-and-text-module-component"
    :style="{
      '--quote-video-and-text-bg':
        props.data?.background_color || 'rgba(255, 255, 255, 1)',
    }">
    <template v-if="props.data.enable_quote">
      <TextRevealComponent
        :data="{
          text: props.data.quote_content,
          mini_heading: '',
          use_quotes_version: true,
          horizontal_padding: true,
          color_settings: {
            mini_heading_text_color: '#1E6B5C',
            text_color: '#70FFE5',
            bg_color: 'transparent',
            highlight_color: '#0E4343',
          },
        }" />
    </template>
    <div class="rr-container bottom-container">
      <!-- Video -->
      <div
        v-if="props.data.add_video"
        class="video-container">
        <transition name="overlay-fade">
          <div
            v-if="!videoStarted"
            class="play-overlay"
            @click="startPlayback">
            <img
              v-if="posterImageUrl"
              class="overlay-poster"
              :src="posterImageUrl"
              :alt="posterImageAlt" />

            <div class="play-icon">
              <img src="@/assets/img/ras_play_button.png" />
            </div>

            <p
              v-if="props.data.video_information.video_label"
              class="video-label"
              v-html="data.video_information.video_label"></p>
          </div>
        </transition>

        <iframe
          v-if="isVimeo && vimeoEmbedUrl"
          class="video-player"
          :src="vimeoEmbedUrl"
          frameborder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowfullscreen
          title="Vimeo video"></iframe>

        <video
          v-else
          ref="nativeVideoRef"
          class="video-player"
          :src="props.data.video_information.video_file.url"
          :poster="props.data.video_information.poster_image.url"
          :controls="videoStarted"
          playsinline
          preload="metadata"
          @play="onNativePlay" />
      </div>

      <!-- Quote and Text -->
      <div
        v-if="data.enable_text_with_images_section"
        class="quote-text">
        <!-- Top -->
        <div
          ref="leftImageContainerRef"
          class="top">
          <div
            ref="leftImageRef"
            class="image-container">
            <img
              class="top-image"
              :src="data.text_with_images_section.top_image.url"
              :alt="data.text_with_images_section.top_image.alt" />
          </div>

          <div class="content">
            <p
              v-if="data.text_with_images_section.top_text"
              v-html="data.text_with_images_section.top_text"></p>
          </div>
        </div>

        <!-- Bottom -->
        <div
          ref="rightImageContainerRef"
          class="bottom">
          <div
            ref="rightImageRef"
            class="image-container">
            <svg
              class="quote-svg left"
              xmlns="http://www.w3.org/2000/svg"
              width="98"
              height="98"
              viewBox="0 0 98 98"
              fill="none">
              <rect
                x="98"
                y="50.0576"
                width="47.9424"
                height="98"
                transform="rotate(90 98 50.0576)"
                fill="#13D377" />
              <rect
                x="2.38262e-05"
                width="47.9424"
                height="98"
                fill="#13D377" />
            </svg>

            <img
              class="bottom-image"
              :src="data.text_with_images_section.bottom_image.url"
              :alt="data.text_with_images_section.bottom_image.alt" />

            <svg
              class="quote-svg right"
              xmlns="http://www.w3.org/2000/svg"
              width="98"
              height="98"
              viewBox="0 0 98 98"
              fill="none">
              <rect
                x="4.28372e-06"
                y="47.9425"
                width="47.9424"
                height="98"
                transform="rotate(-90 4.28372e-06 47.9425)"
                fill="#13D377" />
              <rect
                x="98"
                y="98.0001"
                width="47.9424"
                height="98"
                transform="rotate(180 98 98.0001)"
                fill="#13D377" />
            </svg>
          </div>

          <div class="content">
            <div class="divider-container">
              <BicolorLine
                :bigger="true"
                left="approachable"
                right="progressive" />
            </div>

            <div
              v-if="data.text_with_images_section.bottom_text"
              v-html="data.text_with_images_section.bottom_text"></div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
/* #META
 {
  "NAME": "QuoteVideoAndTextComponent",
  "CLASSNAME": "quote-video-and-text-module-component",
  "URL": "http://vuevitewp.local/test"
 }
#ENDMETA */

/*
 * Insert your logic here
 */
import TextRevealComponent from '@/components/modules/TextRevealComponent.vue'
import { computed, ref, onBeforeUnmount, onMounted, watch } from 'vue'
import BicolorLine from '@/components/utility/BicolorLine.vue'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import useUtils from '@/components/composables/useUtils.js'
import useFooterBackgroundClass from '@/components/composables/useFooterBackgroundClass.js'

gsap.registerPlugin(ScrollTrigger)

const balancedFooterClass = 'quote-video-balanced-footer'

const leftImageContainerRef = ref(null)
const rightImageContainerRef = ref(null)
const leftImageRef = ref(null)
const rightImageRef = ref(null)
const leftTweenRef = ref(null)
const rightTweenRef = ref(null)
const stopDeviceWatchRef = ref(null)
const { device } = useUtils()

const props = defineProps(['data'])
const nativeVideoRef = ref(null)
const videoStarted = ref(false)

useFooterBackgroundClass(
  balancedFooterClass,
  () => props.data?.update_footer_background
)

const isVimeo = computed(() =>
  Boolean(props.data?.video_information?.use_vimeo)
)

const posterImageUrl = computed(
  () => props.data?.video_information?.poster_image?.url || ''
)

const posterImageAlt = computed(
  () => props.data?.video_information?.poster_image?.alt || 'Video poster'
)

const getVimeoRawUrl = () => {
  const videoInfo = props.data?.video_information || {}

  return (
    videoInfo.vimeo_url ||
    videoInfo.vimeo_video ||
    videoInfo.video_url ||
    videoInfo.video_file?.url ||
    ''
  )
}

const getVimeoId = (url) => {
  if (!url) return ''

  const cleaned = String(url).trim()
  const idMatch = cleaned.match(/(?:vimeo\.com\/(?:video\/)?)(\d+)/)
  if (idMatch && idMatch[1]) return idMatch[1]

  if (/^\d+$/.test(cleaned)) return cleaned
  return ''
}

const vimeoEmbedUrl = computed(() => {
  if (!isVimeo.value) return ''

  const vimeoId = getVimeoId(getVimeoRawUrl())
  if (!vimeoId) return ''

  const controls = videoStarted.value ? 1 : 0
  const autoplay = videoStarted.value ? 1 : 0

  return `https://player.vimeo.com/video/${vimeoId}?controls=${controls}&autoplay=${autoplay}&muted=0&playsinline=1&title=0&byline=0&portrait=0&dnt=1`
})

const onNativePlay = () => {
  if (!videoStarted.value) {
    videoStarted.value = true
  }
}

const startPlayback = () => {
  if (videoStarted.value) return

  if (isVimeo.value) {
    videoStarted.value = true
    return
  }

  if (nativeVideoRef.value) {
    nativeVideoRef.value.play().catch(() => {
      // Ignore autoplay/gesture blocking; user can press play again.
    })
  }
}

onMounted(() => {
  const killImageTweens = () => {
    if (leftTweenRef.value?.scrollTrigger)
      leftTweenRef.value.scrollTrigger.kill()
    if (rightTweenRef.value?.scrollTrigger)
      rightTweenRef.value.scrollTrigger.kill()
    if (leftTweenRef.value) leftTweenRef.value.kill()
    if (rightTweenRef.value) rightTweenRef.value.kill()
    leftTweenRef.value = null
    rightTweenRef.value = null
  }

  const setupImageParallax = () => {
    if (!leftImageContainerRef.value || !rightImageContainerRef.value) return

    killImageTweens()

    const rightTargetY =
      device.value === 'mobile' || device.value === 'tablet' ? 0 : -50

    gsap.set(leftImageRef.value, { y: 40 })

    leftTweenRef.value = gsap.to(leftImageRef.value, {
      y: 0,
      ease: 'none',
      scrollTrigger: {
        trigger: leftImageContainerRef.value,
        scrub: true,
        start: 'top bottom',
        end: 'center center',
        markers: false,
      },
    })

    if (rightImageRef.value) {
      gsap.set(rightImageRef.value, { y: 40 })

      rightTweenRef.value = gsap.to(rightImageRef.value, {
        y: rightTargetY,
        ease: 'none',
        scrollTrigger: {
          trigger: rightImageContainerRef.value,
          scrub: true,
          start: 'top bottom',
          end: 'center center',
          markers: false,
        },
      })
    }
  }

  setupImageParallax()

  stopDeviceWatchRef.value = watch(
    device,
    () => {
      setupImageParallax()
    },
    { flush: 'post' }
  )
})

onBeforeUnmount(() => {
  if (stopDeviceWatchRef.value) stopDeviceWatchRef.value()
  if (leftTweenRef.value?.scrollTrigger) leftTweenRef.value.scrollTrigger.kill()
  if (rightTweenRef.value?.scrollTrigger)
    rightTweenRef.value.scrollTrigger.kill()
  if (leftTweenRef.value) leftTweenRef.value.kill()
  if (rightTweenRef.value) rightTweenRef.value.kill()
})
</script>

<style lang="scss" scoped>
.quote-video-and-text-module-component {
  background-color: var(--quote-video-and-text-bg);
  @apply relative z-3 pt-10 pb-1 lg:pt-32;

  &:before {
    @apply absolute bg-balanced top-0 left-0 w-full h-full z-[-1] pointer-events-none;
    content: '';

    @apply rounded-tl-[50px] md:rounded-tl-[60px] lg:rounded-tl-[80px];
  }

  .rr-container {
    &.bottom-container {
      @apply pt-6 pb-10 lg:pt-[128px];

      .video-container {
        @apply relative lg:px-[80px];

        .play-overlay {
          @apply absolute inset-0 z-20 cursor-pointer;
          @apply lg:left-[80px] lg:right-[80px];
          @apply overflow-hidden rounded-[12px] lg:rounded-[27px];
        }

        .overlay-fade-enter-active,
        .overlay-fade-leave-active {
          transition: opacity 0.45s ease;
        }

        .overlay-fade-enter-from,
        .overlay-fade-leave-to {
          opacity: 0;
        }

        .overlay-poster {
          @apply absolute inset-0 h-full w-full transition-opacity object-cover object-center;
        }

        .play-icon {
          @apply absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80px] h-[80px] lg:w-[157px] lg:h-[157px] z-10;
        }

        .video-label {
          @apply z-2 w-[calc(100%_-_32px)] uppercase absolute left-[32px] bottom-[32px] text-left text-[18px] leading-[22px] font-medium text-white/50;
          @apply lg:text-[36px] lg:left-[32px];
        }

        .video-player {
          @apply relative block w-full rounded-[12px] lg:rounded-[27px];
        }

        iframe.video-player {
          aspect-ratio: 16 / 9;
          height: auto;
        }
      }

      .quote-text {
        @apply relative mt-10 lg:mt-[70px];

        .top,
        .bottom {
          @apply flex flex-col gap-7 lg:flex-row lg:gap-14;

          .image-container {
            @apply relative w-full lg:w-[280px];
            img {
              @apply w-full rounded-[27px] object-cover object-center;

              &.top-image {
                @apply h-[284px] max-w-[400px] ml-0;
                @apply lg:max-w-none lg:w-[400px] lg:h-full;
                @apply xl:min-h-[584px] xl:w-full;
              }

              &.bottom-image {
                @apply h-[284px] w-full max-w-[500px] ml-0;
                @apply lg:max-w-none lg:w-[270px] lg:h-[270px];
                @apply xl:w-[370px] xl:h-[370px];
              }
            }
          }

          .content {
            @apply w-full;

            .divider-container {
              @apply mb-9;
            }

            :deep(*) {
              @apply text-white;
            }

            :deep(p) {
              @apply text-[16px] leading-[1.2] mb-5 lg:text-[22px];
            }

            :deep(h1),
            :deep(h2),
            :deep(h3),
            :deep(h4),
            :deep(h5),
            :deep(h6) {
              @apply header-2 mb-6 lg:mb-8;
            }
          }
        }

        .top {
          @apply xl:min-h-[587px];

          .image-container {
            @apply order-2 lg:order-1 lg:w-[400px] lg:flex-[1_0_auto] xl:w-[500px];
          }

          .content {
            @apply px-4 order-1 lg:order-2 lg:pb-20 lg:px-0;
          }
        }

        .bottom {
          @apply mt-10 gap-2 lg:mt-0 lg:pl-[127px] xl:pl-[187px];

          .image-container {
            @apply order-2 w-fit px-[48px];
            @apply lg:order-1 lg:w-fit lg:h-fit lg:flex-[1_0_auto];

            .quote-svg {
              @apply absolute w-[43px] h-[43px] z-1 lg:w-[98px] lg:h-[98px];

              &.left {
                @apply bottom-[-12px] left-[37px] lg:bottom-[-24px] lg:left-[14px];
              }
              &.right {
                @apply top-[-12px] right-[37px] lg:top-[-24px] lg:right-[14px];
              }
            }
          }

          .content {
            @apply px-4 order-1 lg:order-2 xl:pl-10 xl:pr-0;
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
body.quote-video-balanced-footer .site-footer {
  background: #1e6b5c !important;
}
</style>
